import { Component, Input, ElementRef, OnDestroy, OnInit, HostListener } from '@angular/core';
import { HoverService } from './hover.service';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'tog-hover',
  templateUrl: 'hover.template.html',
  styleUrls: ['hover.scss'],
})
export class HoverComponent implements OnInit, OnDestroy {
  id: number;

  @Input() debug: boolean = false;

  // Overall settings
  @Input() offsetLeft: number = 0;
  @Input() offsetTop: number = 0;
  @Input() dismissable: boolean = false;

  // Setting one of these will cause the hover to ignore whether or not
  // it is above/below the middle of the page
  @Input() alwaysExtendUp: boolean = false;
  @Input() alwaysExtendDown: boolean = false;

  // Arrow settings
  @Input() showArrow: boolean = true;
  @Input() arrowOffsetLeft: number = 0;

  hoverShown: boolean = false;

  // Controls CSS class; extend-up for a hover with arrow on the bottom,
  // extend-down for a hover with arrow on the top
  hoverType: string = '';

  // If set to true, will make extend-down arrow be purple to match title element
  hasTitle: boolean = false;

  // Controls hover style
  hoverStyle: { width: string, height: string, left: string, top: string, bottom: string } =
              { width: '340px', height: 'auto', left: '', top: '', bottom: '' };

  timeout: number;
  onHoverChangeSubscription: Subscription;

  hoverTarget: any;
  hoverBody: any;
  hoverWrapper: any;

  constructor(private _el: ElementRef, private hoverService: HoverService) {
    this.id = this.hoverService.generateHoverID();
  }

  @Input() set width(width: number) {
    if (width !== -1) this.hoverStyle.width = width + 'px';
    else this.hoverStyle.width = '340px';
  }

  @Input() set height(height: number) {
    if (height !== -1) this.hoverStyle.height = height + 'px';
    else this.hoverStyle.height = 'auto';
  }

  ngOnInit() {
    let self = this;
    this.hoverBody = this._el.nativeElement;
    this.hoverWrapper = this.hoverBody.getElementsByClassName('hover-wrapper')[0];
    this.hoverTarget = this._el.nativeElement.parentNode;
    document.body.appendChild(self.hoverBody);

    // This simply checks if we have a hover title. If so, the extend-down arrow should be purple
    if (this.hoverBody.getElementsByClassName('tog-hover-title').length) {
      this.hasTitle = true;
    }

    this.hoverTarget.onmouseenter = function() {
      self.preventHidinghover();
      self.showHover();
    };

    this.hoverBody.onmouseenter = function() {
      self.preventHidinghover();
    };

    this.hoverTarget.onmouseleave = function() {
      self.attemptToHideHover();
    };

    this.hoverBody.onmouseleave = function() {
      self.attemptToHideHover();
    };

    // If another hover is shown, dismiss this hover
    this.onHoverChangeSubscription = this.hoverService.onHoverShow.subscribe(id => {
      if (id !== self.id && this.hoverShown) {
        this.attemptToHideHover(true);
      }
    });
  }

  showHover() {
    this.hoverShown = true;
    this.hoverService.showHover(this.id);

    // Prevent body scrolling while hover is active
    this.disablePageScroll();

    // Check if the hover is in the top half of the page; if so, we should use the extend-up class
    // Otherwise, if it's in the bottom half, use extend-down
    // Ignore if alwaysExtendUp or alwaysExtendDown are set
    this.hoverType = this.shouldExtendUp() ? 'extend-up' : 'extend-down';
    this.calculatePagePosition();
  }

  disablePageScroll() {
    let root = document.getElementsByClassName('tog-root-content');
    if (root.length) {
      (<any>root[0]).style.paddingRight = this.calculateScrollbarWidth() + 'px';
      root[0].classList.add('hover-active');
    }
  }

  enablePageScroll() {
    let root = document.getElementsByClassName('tog-root-content');
    if (root.length) {
      (<any>root[0]).style.paddingRight = '0';
      root[0].classList.remove('hover-active');
    }
  }

  // From https://stackoverflow.com/questions/13382516/getting-scroll-bar-width-using-javascript
  calculateScrollbarWidth() {
    var outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.width = "100px";
    outer.style.msOverflowStyle = "scrollbar"; // needed for WinJS apps

    document.body.appendChild(outer);

    var widthNoScroll = outer.offsetWidth;

    // force scrollbars
    outer.style.overflow = "scroll";

    // add innerdiv
    var inner = document.createElement("div");
    inner.style.width = "100%";
    outer.appendChild(inner);

    var widthWithScroll = inner.offsetWidth;

    // remove divs
    outer.parentNode.removeChild(outer);

    return widthNoScroll - widthWithScroll;
  }

  calculatePagePosition() {
    let elementBounds = this.hoverTarget.getBoundingClientRect();

    // If we're showing the arrow we need to add 20px
    let arrowOffset = this.showArrow ? 20 : 0;

    // Calculate position from top of page depending on whether or not we should extend up
    if (this.shouldExtendUp()) {
      // ( Window Height - Target Bottom ) + Target Height - Top Offset
      let bottom = window.innerHeight - elementBounds.bottom;
      this.hoverStyle.bottom = bottom + arrowOffset + elementBounds.height - this.offsetTop + 'px';
      this.hoverStyle.top = '';
    } else {
      // Target Y + Target Height + 50 for arrow + Top Offset
      this.hoverStyle.top = elementBounds.top + elementBounds.height + arrowOffset + this.offsetTop + 'px';
      this.hoverStyle.bottom = '';
    }

    // Calculate position from left of page
    // Target X + ( Target Width / 2 ) + Left Offset
    this.hoverStyle.left = elementBounds.left +
                           this.offsetLeft -
                           (this.hoverWrapper.getBoundingClientRect().width / 2) +
                           (elementBounds.width / 2) + 'px';
  }

  attemptToHideHover(forceHide?: boolean) {
    let self = this;
    let hide = function() {
      if (!self.debug) {
        self.hoverShown = false;
        self.hoverType = '';

        // Allow scrolling
        self.enablePageScroll();
      }
    };

    if (forceHide) hide();
    else {
      clearTimeout(this.timeout);
      this.timeout = <any>setTimeout(() => {
        hide();
      }, 200);
    }
  }

  preventHidinghover() {
    clearTimeout(this.timeout);
  }

  shouldExtendUp(): boolean {
    if (this.alwaysExtendUp) return true;
    if (this.alwaysExtendDown) return false;
    else {
      let elementBounds = this.hoverTarget.getBoundingClientRect();
      return (elementBounds.top > (window.innerHeight / 2));
    }
  }

  ngOnDestroy() {
    if (this.hoverBody) {
      document.body.removeChild(this.hoverBody);
    }
    if (this.onHoverChangeSubscription) {
      this.onHoverChangeSubscription.unsubscribe();
    }
    this.hoverService.removeHoverID(this.id);
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/hover/hover.component.ts